import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useProfile } from '../context/ProfileContext';
import { useT } from '../i18n/useT';
import { F, DUR } from '../styles/theme';
import { Icon, Icons } from './red/Icons';
import CoinImage from './CoinImage';
import { COIN_MAP } from '../utils/coingecko';


// Items sind statische Navigations-/Aktions-Sprünge.
function buildItems({ onTabChange, onOpenAlerts, isDark, setIsDark, language, setLanguage, isDE, t }) {
  // Icons exakt wie in der NavBar (ICON_CUSTOM Multi-Shape):
  //   home (2×2 grid), walletNav (buy/sell arrows), shieldCheck (haltefrist),
  //   profile, plus, bell, themeMoon/sun, language (Sprechblase)
  const gPages = t('cmd_group_pages');
  const gActions = t('cmd_group_actions');
  const items = [
    { group: gPages, label: t('dashboard'),      icon: Icons.home,        run: () => onTabChange('dashboard'), kws: ['dashboard', 'übersicht', 'overview', 'start', 'home'] },
    { group: gPages, label: t('transactions'),   icon: Icons.walletNav,   run: () => onTabChange('wallet'),    kws: ['transaktionen', 'transactions', 'wallet', 'tx', 'liste', 'list'] },
    ...(isDE ? [{ group: gPages, label: t('smart_check'),   icon: Icons.shieldCheck, run: () => onTabChange('berichte'),  kws: ['smart check', 'berichte', 'reports', 'report', 'haltefrist', 'holding', 'pdf', 'excel'] }] : []),
    { group: gPages, label: t('profile'),        icon: Icons.profile,     run: () => onTabChange('settings'),  kws: ['profil', 'profile', 'konto', 'account', 'einstellungen', 'settings'] },

    { group: gActions, label: t('add_tx'),                   icon: Icons.plus,  run: () => onTabChange('add'),     kws: ['add', 'neu', 'new', 'kauf', 'buy', 'verkauf', 'sell', 'swap'] },
    { group: gActions, label: t('cmd_open_alerts'),          icon: Icons.bell,  run: () => onOpenAlerts?.(),       kws: ['alerts', 'benachrichtigung', 'notification', 'preis', 'price'] },
    { group: gActions,
      label: isDark ? t('cmd_light_mode') : t('cmd_dark_mode'),
      icon: isDark ? Icons.sun : Icons.themeMoon,
      run: () => setIsDark(!isDark),
      kws: ['theme', 'dark', 'light', 'mode', 'nachtmodus', 'tagmodus'],
    },
    { group: gActions,
      label: language === 'de' ? t('cmd_lang_en') : t('cmd_lang_de'),
      icon: Icons.language,
      run: () => setLanguage(language === 'de' ? 'en' : 'de'),
      kws: ['sprache', 'language', 'english', 'englisch', 'deutsch', 'german', 'de', 'en'],
    },
  ];
  return items;
}

function score(item, q) {
  if (!q) return 0;
  const ql = q.toLowerCase();
  if (item.label.toLowerCase().startsWith(ql)) return 100;
  if (item.label.toLowerCase().includes(ql)) return 60;
  for (const kw of (item.kws || [])) {
    if (kw.startsWith(ql)) return 40;
    if (kw.includes(ql)) return 20;
  }
  return -1;
}

// Eindeutige Coin-Symbole aus den Transaktionen ziehen (kauf/verkauf/swap-IN).
// Ergebnis: Liste { sym, name, coinId, marketCap-Proxy }, max 20 Einträge.
function deriveCoinsFromTxs(txs, marketData) {
  if (!Array.isArray(txs) || !txs.length) return [];
  const seen = new Set();
  const out = [];
  for (const tx of txs) {
    const candidates = tx.typ === 'swap'
      ? [tx.coinIn, tx.coinOut].filter(Boolean)
      : [tx.coin].filter(Boolean);
    for (const sym of candidates) {
      const up = String(sym).toUpperCase();
      if (seen.has(up)) continue;
      seen.add(up);
      const meta = COIN_MAP[up] || {};
      const coinId = meta.id || (tx.typ === 'swap'
        ? (tx.coinIn === sym ? tx.coinIdIn : tx.coinIdOut)
        : tx.coinId);
      out.push({
        sym: up,
        name: meta.name || up,
        coinId,
      });
    }
  }
  // Sort: wenn marketCap-Proxy via marketData? — wir sortieren alphabetisch,
  // weil holdings-Wert hier keine schnelle Quelle hat.
  out.sort((a, b) => a.sym.localeCompare(b.sym));
  return out.slice(0, 20);
}

export default function CommandPalette({ open, onClose, onTabChange, onOpenAlerts, txs, marketData, onSelectAsset }) {
  const { T, isDark, setIsDark } = useTheme();
  const { language, setLanguage, country } = useProfile();
  const { t } = useT();
  const isDE = country === 'DE';
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);
  const inputRef = useRef(null);

  const coinItems = useMemo(() => {
    if (!onSelectAsset) return [];
    return deriveCoinsFromTxs(txs, marketData).map(c => ({
      group: t('cmd_group_coins'),
      label: c.name,
      sub: c.sym,
      coin: c, // für Icon
      run: () => onSelectAsset({ sym: c.sym, coinId: c.coinId, name: c.name }),
      kws: [c.sym.toLowerCase(), c.name.toLowerCase()],
    }));
  }, [txs, marketData, onSelectAsset, t]);

  const allItems = useMemo(
    () => [
      ...buildItems({ onTabChange, onOpenAlerts, isDark, setIsDark, language, setLanguage, isDE, t }),
      ...coinItems,
    ],
    [onTabChange, onOpenAlerts, isDark, setIsDark, language, setLanguage, isDE, coinItems, t]
  );

  const filtered = useMemo(() => {
    if (!q) return allItems;
    return allItems
      .map(it => ({ it, s: score(it, q) }))
      .filter(x => x.s >= 0)
      .sort((a, b) => b.s - a.s)
      .map(x => x.it);
  }, [allItems, q]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setQ('');
      setIdx(0);
      // focus after paint
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // clamp index when filtered changes
  useEffect(() => {
    if (idx >= filtered.length) setIdx(Math.max(0, filtered.length - 1));
  }, [filtered.length, idx]);

  const runIdx = (i) => {
    const item = filtered[i];
    if (!item) return;
    onClose?.();
    setTimeout(() => item.run(), 0); // close vor Navigation, damit Modal weg ist
  };

  // Keyboard inside the palette
  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => Math.min(filtered.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx(i => Math.max(0, i - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); runIdx(idx); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose?.(); }
  };

  if (!open) return null;

  // Grouped render
  const groups = [];
  let lastGroup = null;
  filtered.forEach((it, i) => {
    if (it.group !== lastGroup) {
      groups.push({ name: it.group, items: [] });
      lastGroup = it.group;
    }
    groups[groups.length - 1].items.push({ it, i });
  });

  return (
    <div
      onClick={onClose}
      data-kb-overlay
      style={{
        position: 'fixed', inset: 0, zIndex: 3000,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: '14vh', paddingBottom: 'var(--kbpad)',
        transition: 'padding-bottom var(--dur-kb) var(--ease-kb)',
        background: 'rgba(8,8,11,0.66)',
        backdropFilter: 'blur(10px) saturate(120%)',
        WebkitBackdropFilter: 'blur(10px) saturate(120%)',
        animation: `cmdPaletteFade ${DUR.fast}ms ease both`,
      }}
    >
      <style>{`
        @keyframes cmdPaletteFade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes cmdPaletteIn {
          from { transform: translateY(6px) scale(0.965); opacity: 0 }
          to   { transform: translateY(0)   scale(1);     opacity: 1 }
        }
        .cmdPaletteCard { animation: cmdPaletteIn 260ms cubic-bezier(0.23, 1, 0.32, 1) both; }
        @media (prefers-reduced-motion: reduce) {
          .cmdPaletteCard { animation: cmdPaletteFade ${DUR.fast}ms ease both; }
        }
      `}</style>

      <div
        className="cmdPaletteCard"
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 560,
          background: T.surface,
          border: `1px solid ${T.line}`,
          borderRadius: 18,
          boxShadow: T.cardShadowLg,
          overflow: 'hidden',
          fontFamily: F.family,
        }}
      >
        {/* Input row */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '14px 16px',
          borderBottom: `1px solid ${T.line}`,
        }}>
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none"
            stroke={T.sub} strokeWidth={2} strokeLinecap="round">
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
          </svg>
          <input
            ref={inputRef}
            value={q}
            onChange={e => { setQ(e.target.value); setIdx(0); }}
            onKeyDown={onKey}
            placeholder={t('cmd_placeholder')}
            style={{
              flex: 1, background: 'transparent', border: 'none', outline: 'none',
              color: T.text, fontSize: 15, fontFamily: F.family, letterSpacing: '-0.01em',
            }}
          />
          <CloseRoundBtn T={T} onClick={onClose} />
        </div>

        {/* Results */}
        <div style={{ maxHeight: '50vh', overflowY: 'auto', padding: '6px 0' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '24px 16px', fontSize: 13, color: T.sub, textAlign: 'center' }}>
              {t('cmd_no_results')}
            </div>
          ) : groups.map(g => (
            <div key={g.name}>
              <div style={{ padding: '10px 16px 4px' }}>
                <span style={{
                  fontFamily: F.family, fontSize: 11.5, fontWeight: 600,
                  letterSpacing: '-0.005em', color: T.sub,
                }}>{g.name}</span>
              </div>
              {g.items.map(({ it, i }) => {
                const active = i === idx;
                return (
                  <button
                    key={i}
                    className="mo-row"
                    onMouseEnter={() => setIdx(i)}
                    onClick={() => runIdx(i)}
                    style={{
                      width: '100%',
                      display: 'flex', alignItems: 'center', gap: 14,
                      padding: '11px 16px',
                      background: active ? T.itemHover : 'transparent',
                      border: 'none', borderLeft: active ? `2px solid ${T.text}` : '2px solid transparent',
                      color: T.text, cursor: 'pointer',
                      fontFamily: F.family, fontSize: 13.5, fontWeight: 500,
                      letterSpacing: '-0.01em',
                      textAlign: 'left',
                      WebkitTapHighlightColor: 'transparent',
                      transition: `background ${DUR.fast}ms, color ${DUR.fast}ms`,
                    }}
                  >
                    <span className="mo-lead" style={{ display: 'inline-flex', flexShrink: 0 }}>
                      {it.coin ? (
                        <CoinImage sym={it.coin.sym} marketData={marketData} size={22} />
                      ) : (
                        <Icon d={it.icon} size={19} stroke={active ? T.text : T.sub} sw={1.8} />
                      )}
                    </span>
                    <span style={{ flex: 1 }}>{it.label}</span>
                    {it.sub && (
                      <span style={{ fontFamily: F.family, fontSize: 11, fontWeight: 700, color: T.sub, letterSpacing: '0.04em' }}>
                        {it.sub}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Runder X-Schließen-Button — Hover-Treatment wie Nav (T.itemHover,
// neutral je nach Tag/Nacht-Modus statt rot)
function CloseRoundBtn({ T, onClick }) {
  const { t } = useT();
  const [hover, setHover] = useState(false);
  return (
    <button
      className="mo-close"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label={t('close')}
      style={{
        width: 30, height: 30, borderRadius: 999,
        background: hover ? T.itemHover : 'transparent',
        border: `1px solid ${T.line}`,
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
        transition: `background ${DUR.fast}ms, color ${DUR.fast}ms`,
        padding: 0,
      }}
    >
      <svg className="mo-close-ico" width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke={hover ? T.text : T.sub} strokeWidth="2" strokeLinecap="round">
        <path d="M18 6L6 18M6 6l12 12" />
      </svg>
    </button>
  );
}

// Hook: bindet Cmd/Ctrl+K an einen Setter.
export function useCommandPaletteHotkey(setOpen) {
  useEffect(() => {
    const onKey = (e) => {
      const isK = e.key === 'k' || e.key === 'K';
      if (isK && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(v => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOpen]);
}
